/**
 * ToolbarIconButton Component
 *
 * Shared icon button component for toolbars and floating map controls.
 * Single source of truth for button styling - eliminates code duplication.
 *
 * Features:
 * - Consistent 40x40px size (lg) across all toolbar levels
 * - Unified hover, active, and disabled states
 * - Responsive tooltips (right on desktop, top on mobile)
 * - 100% CSS Modules
 * - Mantine ActionIcon integration
 * - Support for light and transparent variants
 *
 * Used by:
 * - MapControlsToolbar / MapZoomControls (floating map controls)
 * - ToolButton (MapToolbox Level 1/2 - Toolbar/Settings)
 * - ShapeSelector / LineStyleSelector / LineTypeSelector / FillTypeSelector (Level 3)
 *
 * @module components/ui/ToolbarIconButton
 */

import { memo } from 'react';
import { ActionIcon, Tooltip } from '@mantine/core';
import type { ToolbarIconButtonProps } from './ToolbarIconButton.types';
import classes from './ToolbarIconButton.module.css';

/**
 * ToolbarIconButton Component
 *
 * Reusable icon-only button with consistent styling for toolbars
 */
export const ToolbarIconButton = memo(function ToolbarIconButton({
  icon,
  tooltip,
  active = false,
  disabled = false,
  onClick,
  variant = 'light',
  size = 'lg',
  className = '',
  showIndicator = false,
}: ToolbarIconButtonProps) {
  return (
    <Tooltip label={tooltip} position="right" withArrow disabled={disabled}>
      <ActionIcon
        variant={variant}
        color="gray"
        size={size}
        onClick={onClick}
        disabled={disabled}
        className={`${classes.button} ${active ? classes.active : ''} ${className}`}
        aria-label={tooltip}
        aria-pressed={active}
      >
        <span className={classes.iconWrapper}>
          {icon}
          {showIndicator && <span className={classes.indicatorDot} />}
        </span>
      </ActionIcon>
    </Tooltip>
  );
});
